import { useState, useEffect, useCallback, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Link as LinkIcon, FileText, Loader2, X, Sparkles, FileQuestion, Users, RefreshCw } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useUserKnowledgeBases } from "@/hooks/useKnowledgeBases";
import { GibletGuide } from "./GibletGuide";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "./EmptyState";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { InviteNetworkDialog } from "./InviteNetworkDialog";
import { useSprintRealtime } from "@/hooks/useSprintRealtime";
import { Progress } from "@/components/ui/progress";

interface InformStageProps {
  sprintId: string;
  userId: string;
  onComplete: () => void;
}

export const InformStage = ({ sprintId, userId, onComplete }: InformStageProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: userKbs, isLoading: kbsLoading } = useUserKnowledgeBases(userId);
  const [contentType, setContentType] = useState<'file' | 'kb_link' | 'text' | 'url'>('text');
  const [content, setContent] = useState('');
  const [urlContent, setUrlContent] = useState('');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedKbs, setSelectedKbs] = useState<string[]>([]);
  const [addedContent, setAddedContent] = useState<any[]>([]);
  const [milestone, setMilestone] = useState<string>('');
  const [processingContent, setProcessingContent] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invitationsSent, setInvitationsSent] = useState(0);
  const [sprintObjective, setSprintObjective] = useState("");
  const [viewFilter, setViewFilter] = useState<'my' | 'all'>('my');
  const [allParticipantsContent, setAllParticipantsContent] = useState<any[]>([]);
  const [realtimeIndicator, setRealtimeIndicator] = useState<string | null>(null);
  const [kbTitlesCache, setKbTitlesCache] = useState<Record<string, string>>({});

  // Real-time updates handler
  const handleInformContentUpdate = useCallback((payload: any) => {
    console.log('Real-time inform content update:', payload);
    
    if (payload.eventType === 'INSERT' && payload.new.contributor_id !== userId) {
      setRealtimeIndicator('New content added by a participant');
      setTimeout(() => setRealtimeIndicator(null), 3000);
    }
    
    // Refresh content when viewing all participants
    if (viewFilter === 'all') {
      loadAllParticipantsContent();
    }
    
    // Always refresh own content to stay in sync
    loadAddedContent();
  }, [viewFilter, userId]);

  // Subscribe to real-time updates
  useSprintRealtime(sprintId, undefined, undefined, handleInformContentUpdate);

  useEffect(() => {
    loadAddedContent();
    loadSprintDetails();
  }, [sprintId]);

  useEffect(() => {
    if (viewFilter === 'all') {
      loadAllParticipantsContent();
    }
  }, [viewFilter, sprintId]);

  const loadSprintDetails = async () => {
    try {
      const { data, error } = await supabase
        .from("prompt_architecture_sprints")
        .select("objective")
        .eq("id", sprintId)
        .single();

      if (error) throw error;
      setSprintObjective(data.objective);
    } catch (error) {
      console.error("Error loading sprint details:", error);
    }
  };

  const loadAddedContent = async () => {
    const { data } = await supabase
      .from('sprint_inform_content')
      .select('*')
      .eq('sprint_id', sprintId)
      .eq('contributor_id', userId);
    
    if (data) {
      setAddedContent(data);
      // Fetch KB titles for kb_link items
      await fetchKbTitles(data);
    }
  };

  const fetchKbTitles = async (contentItems: any[]) => {
    const kbLinkItems = contentItems.filter(item => item.content_type === 'kb_link');
    if (kbLinkItems.length === 0) return;

    // Collect all KB IDs (content_reference may contain comma-separated IDs)
    const allKbIds = kbLinkItems.flatMap(item => 
      item.content_reference.split(',').map((id: string) => id.trim())
    );
    const uniqueKbIds = [...new Set(allKbIds)];

    // Fetch titles for all KB IDs not already cached
    const uncachedIds = uniqueKbIds.filter(id => !kbTitlesCache[id]);
    if (uncachedIds.length === 0) return;

    const { data: kbs } = await supabase
      .from('knowledge_bases')
      .select('id, title')
      .in('id', uncachedIds);

    if (kbs) {
      const newCache = { ...kbTitlesCache };
      kbs.forEach(kb => {
        newCache[kb.id] = kb.title;
      });
      setKbTitlesCache(newCache);
    }
  };

  const loadAllParticipantsContent = async () => {
    const { data } = await supabase
      .from('sprint_inform_content')
      .select(`
        *,
        profiles:contributor_id (
          display_name,
          avatar_url
        )
      `)
      .eq('sprint_id', sprintId)
      .order('created_at', { ascending: false });
    
    if (data) {
      setAllParticipantsContent(data);
      // Fetch KB titles for kb_link items
      await fetchKbTitles(data);
    }
  };

  // Helper function to get display label for content type
  const getContentTypeDisplay = (item: any): string => {
    if (item.content_type === 'kb_link') {
      const kbIds = item.content_reference.split(',').map((id: string) => id.trim());
      const titles = kbIds.map((id: string) => kbTitlesCache[id] || 'Loading...');
      return titles.join(', ');
    }
    if (item.content_type === 'file') {
      // Extract filename from the full path
      const pathParts = item.content_reference.split('/');
      return pathParts[pathParts.length - 1];
    }
    switch (item.content_type) {
      case 'url': return 'URL';
      case 'text': return 'Text';
      default: return item.content_type;
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    const filesArray = Array.from(files);
    const invalidFiles = filesArray.filter(f => f.size > maxSize);
    
    if (invalidFiles.length > 0) {
      toast({ 
        title: "Files too large", 
        description: `${invalidFiles.length} file(s) exceed 10MB limit`, 
        variant: "destructive" 
      });
      return;
    }

    setUploading(true);
    setProcessingContent(true);
    setUploadProgress(0);
    
    try {
      const totalFiles = filesArray.length;
      let completed = 0;

      for (const file of filesArray) {
        const filePath = `${userId}/sprint-${sprintId}/${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('knowledge-bases')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: insertData, error: insertError } = await supabase
          .from('sprint_inform_content')
          .insert({
            sprint_id: sprintId,
            contributor_id: userId,
            content_type: 'file',
            content_reference: filePath
          })
          .select()
          .single();

        if (insertError) throw insertError;

        await processContentWithAI(insertData.id, `File: ${file.name}`);
        
        completed++;
        setUploadProgress((completed / totalFiles) * 100);
      }

      await loadAddedContent();
      if (addedContent.length === 0) {
        setMilestone('🎉 First content added!');
        setTimeout(() => setMilestone(''), 3000);
      }
      
      toast({ title: `${totalFiles} file(s) uploaded and processed successfully` });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setProcessingContent(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const processContentWithAI = async (contentId: string, contentText: string) => {
    try {
      await supabase.functions.invoke('process-inform-content', {
        body: { contentId, contentText, contentType }
      });
    } catch (error) {
      console.error('AI processing error:', error);
    }
  };

  const handleAddContent = async () => {
    const currentContent = contentType === 'url' ? urlContent : content;
    
    if (!currentContent && contentType !== 'kb_link') {
      toast({ title: "Please enter content", variant: "destructive" });
      return;
    }

    if (contentType === 'kb_link' && selectedKbs.length === 0) {
      toast({ title: "Please select at least one KB", variant: "destructive" });
      return;
    }

    setUploading(true);
    setProcessingContent(true);

    try {
      const contentRef = contentType === 'kb_link' ? selectedKbs.join(',') : currentContent;
      
      const { data: insertData, error } = await supabase
        .from('sprint_inform_content')
        .insert({
          sprint_id: sprintId,
          contributor_id: userId,
          content_type: contentType,
          content_reference: contentRef
        })
        .select()
        .single();

      if (error) throw error;

      await processContentWithAI(insertData.id, currentContent || `KBs: ${selectedKbs.length}`);
      
      await loadAddedContent();
      setContent('');
      setUrlContent('');
      setSelectedKbs([]);
      
      if (addedContent.length === 0) {
        setMilestone('🎉 First content added!');
        setTimeout(() => setMilestone(''), 3000);
      }
      
      toast({ title: "Content added and processed successfully" });
    } catch (error: any) {
      toast({
        title: "Failed to add content",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      setProcessingContent(false);
    }
  };

  const handleCompleteStage = async () => {
    if (addedContent.length === 0) {
      toast({ 
        title: "No content added",
        description: "Please add at least one piece of content before completing this stage",
        variant: "destructive" 
      });
      return;
    }

    try {
      const { error } = await supabase
        .from('prompt_architecture_sprints')
        .update({ inform_stage_completed: true })
        .eq('id', sprintId);

      if (error) throw error;

      onComplete();
    } catch (error: any) {
      toast({
        title: "Failed to complete stage",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const handleRemoveContent = async (contentId: string) => {
    try {
      const contentItem = addedContent.find(item => item.id === contentId);
      
      const { error } = await supabase
        .from('sprint_inform_content')
        .delete()
        .eq('id', contentId);

      if (error) throw error;

      if (contentItem?.content_type === 'file' && contentItem?.content_reference) {
        const { error: storageError } = await supabase.storage
          .from('knowledge-bases')
          .remove([contentItem.content_reference]);
        
        if (storageError) {
          console.error('Failed to delete file from storage:', storageError);
        }
      }
      
      setAddedContent(prev => prev.filter(item => item.id !== contentId));
      toast({ title: "Content removed" });
    } catch (error: any) {
      toast({ title: "Failed to remove content", variant: "destructive" });
    }
  };

  const toggleKbSelection = (kbId: string) => {
    setSelectedKbs(prev => 
      prev.includes(kbId) ? prev.filter(id => id !== kbId) : [...prev, kbId]
    );
  };

  return (
    <div className="space-y-4">
      <GibletGuide stage="inform" milestone={milestone} />
      
      <Card className="p-6">
        <div className="space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-bold mb-2">Stage 1: Inform</h3>
              <p className="text-muted-foreground">
                Share knowledge to inform your sprint objective. Add files, link knowledge bases, or provide text/URLs.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewFilter === 'my' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewFilter('my')}
              >
                My Content
              </Button>
              <Button
                variant={viewFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setViewFilter('all');
                  loadAllParticipantsContent();
                }}
              >
                All Participants
              </Button>
            </div>
          </div>

          {/* Real-time indicator */}
          {realtimeIndicator && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 rounded-lg border border-primary/20 animate-pulse">
              <RefreshCw className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-primary font-medium">{realtimeIndicator}</span>
            </div>
          )}

          {addedContent.length > 0 && (
            <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium mb-1">Ready to collaborate?</h4>
                  <p className="text-sm text-muted-foreground">
                    Invite your network to contribute to this sprint
                  </p>
                </div>
                <Button 
                  variant="default"
                  onClick={() => setInviteDialogOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Users className="w-4 h-4" />
                  Invite Your Network
                </Button>
              </div>
              {invitationsSent > 0 && (
                <p className="text-sm text-green-600 mt-2">
                  ✓ {invitationsSent} invitation{invitationsSent !== 1 ? 's' : ''} sent
                </p>
              )}
            </div>
          )}

        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            <Button
              variant={contentType === 'file' ? 'default' : 'outline'}
              onClick={() => {
                setContentType('file');
                fileInputRef.current?.click();
              }}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              File
            </Button>
            <Button
              variant={contentType === 'kb_link' ? 'default' : 'outline'}
              onClick={() => setContentType('kb_link')}
              className="flex items-center gap-2"
            >
              <LinkIcon className="w-4 h-4" />
              KB Link
            </Button>
            <Button
              variant={contentType === 'text' ? 'default' : 'outline'}
              onClick={() => setContentType('text')}
              className="flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Text
            </Button>
            <Button
              variant={contentType === 'url' ? 'default' : 'outline'}
              onClick={() => setContentType('url')}
              className="flex items-center gap-2"
            >
              <LinkIcon className="w-4 h-4" />
              URL
            </Button>
          </div>

          {contentType === 'file' && (
            <div className="space-y-2">
              <Label>Upload Files (PDF, DOCX, PPTX, XLSX, TXT, Images - Max 10MB each)</Label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                disabled={uploading || processing}
                accept=".txt,.md,.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.pptx"
                multiple
                className="hidden"
              />
              {uploading && (
                <div className="space-y-1">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-muted-foreground">
                    {processingContent ? 'Processing with AI...' : `Uploading... ${Math.round(uploadProgress)}%`}
                  </p>
                </div>
              )}
            </div>
          )}

          {contentType === 'kb_link' && (
            <div className="space-y-3">
              <Label>Select Knowledge Bases (multiple selection)</Label>
              {kbsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : userKbs && userKbs.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                  {userKbs.map((kb) => (
                    <div key={kb.id} className="flex items-start gap-3 p-2 hover:bg-accent/50 rounded-md">
                      <Checkbox
                        checked={selectedKbs.includes(kb.id)}
                        onCheckedChange={() => toggleKbSelection(kb.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{kb.title}</p>
                        {kb.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">{kb.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          {kb.domains && (
                            <Badge variant="secondary" className="text-xs">
                              {kb.domains.name}
                            </Badge>
                          )}
                          {kb.file_type && (
                            <span className="text-xs text-muted-foreground">{kb.file_type}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState 
                  icon={FileQuestion}
                  title="No Knowledge Bases"
                  description="Create knowledge bases first to link them to your sprint"
                />
              )}
              {selectedKbs.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedKbs.length} KB{selectedKbs.length !== 1 ? 's' : ''} selected
                </p>
              )}
            </div>
          )}

          {contentType === 'text' && (
            <div>
              <Label htmlFor="text-input">Enter Text</Label>
              <Textarea
                id="text-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste your text here..."
                rows={6}
              />
            </div>
          )}

          {contentType === 'url' && (
            <div>
              <Label htmlFor="url-input">Enter URL</Label>
              <Input
                id="url-input"
                type="url"
                value={urlContent}
                onChange={(e) => setUrlContent(e.target.value)}
                placeholder="https://example.com/article"
              />
            </div>
          )}

          {contentType !== 'file' && (
            <Button onClick={handleAddContent} disabled={uploading || processing}>
              {(uploading || processing) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {processing ? 'Processing...' : 'Add Content'}
            </Button>
          )}
        </div>

        {processingContent && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Processing content with AI...
          </div>
        )}

        {viewFilter === 'my' && addedContent.length > 0 && (
          <div className="space-y-2">
            <Label>My Content ({addedContent.length})</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {addedContent.map((item) => (
                <Card key={item.id} className="p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="text-xs">
                          {getContentTypeDisplay(item)}
                        </Badge>
                        {item.ai_processing_status === 'processing' && (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Processing...
                          </Badge>
                        )}
                        {item.ai_processing_status === 'completed' && item.ai_summary && (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            AI Processed
                          </Badge>
                        )}
                        {item.ai_processing_status === 'failed' && (
                          <Badge variant="destructive" className="text-xs">
                            Failed
                          </Badge>
                        )}
                      </div>
                      {item.ai_processing_status === 'failed' && item.ai_processing_error && (
                        <p className="text-xs text-destructive mb-1">
                          Error: {item.ai_processing_error}
                        </p>
                      )}
                      {item.ai_summary && (
                        <p className="text-sm text-muted-foreground mb-2">{item.ai_summary}</p>
                      )}
                      {item.key_concepts && item.key_concepts.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {item.key_concepts.slice(0, 5).map((concept: string, idx: number) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {concept}
                            </Badge>
                          ))}
                        </div>
                      )}
                     </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveContent(item.id)}
                      className="h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {viewFilter === 'all' && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>All Participants' Content ({allParticipantsContent.length})</Label>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={loadAllParticipantsContent}
                className="h-8"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
            {allParticipantsContent.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {allParticipantsContent.map((item) => (
                  <Card key={item.id} className="p-3 border-l-4 border-l-primary/30">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={item.profiles?.avatar_url} />
                        <AvatarFallback className="text-xs">
                          {item.profiles?.display_name?.substring(0, 2).toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">{item.profiles?.display_name}</span>
                          <Badge variant="secondary" className="text-xs">
                            {getContentTypeDisplay(item)}
                          </Badge>
                          {item.ai_summary && (
                            <Badge variant="outline" className="text-xs flex items-center gap-1">
                              <Sparkles className="w-3 h-3" />
                              AI Processed
                            </Badge>
                          )}
                        </div>
                        {item.ai_summary && (
                          <p className="text-sm text-muted-foreground mb-2">{item.ai_summary}</p>
                        )}
                        {item.key_concepts && item.key_concepts.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {item.key_concepts.slice(0, 5).map((concept: string, idx: number) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {concept}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No content from participants yet</p>
                <p className="text-sm">Invite your network to contribute!</p>
              </div>
            )}
          </div>
        )}

        <div className="pt-4 border-t">
          <Button 
            onClick={handleCompleteStage} 
            size="lg" 
            className="w-full"
            disabled={addedContent.length === 0}
          >
            Complete Inform Stage →
          </Button>
        </div>
      </div>

      <InviteNetworkDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        sprintId={sprintId}
        sprintObjective={sprintObjective}
        userId={userId}
        onInvitationsSent={(count) => {
          setInvitationsSent(prev => prev + count);
          loadAddedContent();
        }}
      />
    </Card>
    </div>
  );
};